<template>
  <div class="pa-2">
    <va-card>
      <div class="d-flex justify-content-between align-items-center mb-4">
        <h5 class="mb-0">Actionable Information Report (AIR)</h5>
        <div v-if="crimeData.isAirSubmitted || crimeData.isAirApproved">
          <b-button variant="outline-primary" @click="downloadPDF">
            <i class="fa fa-download mr-1"></i> Download AIR PDF
          </b-button>
        </div>
      </div>

      <div v-if="!isAdmin && !crimeData.isAirSubmitted">
        <!-- Analyst Form -->
        <b-form @submit.prevent="submitAIR">
          <b-form-group label="Information Summary" label-class="font-weight-bold">
            <b-form-textarea
              v-model="airData.infoSummary"
              rows="5"
              placeholder="Enter summary of information..."
              required
            ></b-form-textarea>
          </b-form-group>

          <b-form-group label="Point of Contact" label-class="font-weight-bold">
            <b-form-input
              v-model="airData.pointOfContact"
              placeholder="Details of source/contact..."
              required
            ></b-form-input>
          </b-form-group>

          <b-form-group label="Location Details" label-class="font-weight-bold">
            <b-form-input
              v-model="airData.location.exactLocation"
              placeholder="Specific coordinates or landmarks..."
              required
            ></b-form-input>
          </b-form-group>

          <b-form-group label="Supporting Evidence (Documents, Images or Videos)" label-class="font-weight-bold">
            <div class="custom-file-upload border p-3 rounded bg-white text-center mb-3">
              <input 
                type="file" 
                id="air-file-upload" 
                ref="airFileInput"
                multiple 
                accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip" 
                @change="handleFileUpload" 
                style="display: none;" 
              />
              <b-button variant="outline-primary" @click="$refs.airFileInput.click()" :disabled="isUploading">
                <i class="fa fa-spinner fa-spin mr-2" v-if="isUploading"></i>
                <i class="fa fa-upload mr-2" v-else></i>
                {{ isUploading ? 'Uploading...' : 'Choose Files' }}
              </b-button>
              <div class="small text-muted mt-2">
                Supported formats: Images, Videos, PDF, Word, Excel, ZIP (Max 150MB)
              </div>
            </div>

            <!-- Uploaded Files List -->
            <div v-if="airData.supportingEvidence && airData.supportingEvidence.length" class="mt-3">
              <h6 class="font-weight-bold mb-2">Uploaded Files:</h6>
              <b-row>
                <b-col cols="12" md="6" v-for="(file, index) in airData.supportingEvidence" :key="index" class="mb-2">
                  <div class="d-flex align-items-center p-2 border rounded bg-light">
                    <!-- Icon or Thumbnail -->
                    <div class="mr-2">
                      <img 
                        v-if="file.fileType === 'Image'" 
                        :src="getFileUrl(file.url)" 
                        style="width: 40px; height: 40px; object-fit: cover;" 
                        class="rounded border" 
                      />
                      <i v-else-if="file.fileType === 'Video'" class="fa fa-file-video-o fa-2x text-primary px-2"></i>
                      <i v-else class="fa fa-file-text-o fa-2x text-secondary px-2"></i>
                    </div>
                    
                    <!-- Details -->
                    <div class="flex-grow-1 overflow-hidden">
                      <div class="text-truncate font-weight-bold small" :title="file.description">{{ file.description || 'Uploaded File' }}</div>
                      <div class="text-muted small">{{ file.fileType }}</div>
                    </div>

                    <!-- Remove -->
                    <div>
                      <b-button size="sm" variant="link" class="text-danger p-1" @click="removeEvidence(index)">
                        <i class="fa fa-trash mr-1"></i>
                      </b-button>
                    </div>
                  </div>
                </b-col>
              </b-row>
            </div>
          </b-form-group>

          <b-form-group label="Supporting Evidence Links (Optional)" label-class="font-weight-bold" description="Separate multiple external URLs with commas">
            <b-form-textarea
              v-model="evidenceLinks"
              rows="2"
              placeholder="https://example.com/image1, https://example.com/video1..."
            ></b-form-textarea>
          </b-form-group>

          <div class="mt-4 text-right">
            <b-button type="submit" variant="success" class="px-4" :disabled="isUploading">
              Submit for Final Approval
            </b-button>
          </div>
        </b-form>
      </div>

      <div v-else-if="crimeData.isAirSubmitted || crimeData.isAirApproved">
        <!-- Data Display -->
        <div class="p-3 border rounded bg-light mb-3">
          <b-row class="mb-2">
            <b-col cols="3" class="font-weight-bold">Report ID:</b-col>
            <b-col class="text-primary font-weight-bold">{{ crimeData.airData.airUniqueId }}</b-col>
          </b-row>
          <b-row class="mb-2">
            <b-col cols="3" class="font-weight-bold">Report Status:</b-col>
            <b-col>
              <va-badge v-if="crimeData.isAirApproved" color="success">APPROVED</va-badge>
              <va-badge v-else color="warning">PENDING APPROVAL</va-badge>
            </b-col>
          </b-row>
          <hr />
          <b-row class="mb-2">
            <b-col cols="12" class="font-weight-bold">Information Summary:</b-col>
            <b-col cols="12" class="mt-1 text-muted">{{ crimeData.airData.infoSummary }}</b-col>
          </b-row>
          <b-row class="mb-2">
            <b-col cols="12" class="font-weight-bold">Point of Contact:</b-col>
            <b-col cols="12" class="mt-1 text-muted">{{ crimeData.airData.pointOfContact }}</b-col>
          </b-row>
          <b-row class="mb-2">
            <b-col cols="12" class="font-weight-bold">Location Details:</b-col>
            <b-col cols="12" class="mt-1 text-muted">{{ crimeData.airData.location ? crimeData.airData.location.exactLocation : 'N/A' }}</b-col>
          </b-row>
          <b-row class="mb-2">
            <b-col cols="12" class="font-weight-bold">Supporting Evidence:</b-col>
            <b-col cols="12" class="mt-2">
              <div v-if="crimeData.airData.supportingEvidence && crimeData.airData.supportingEvidence.length">
                <b-row>
                  <b-col cols="12" md="6" lg="4" v-for="(item, index) in crimeData.airData.supportingEvidence" :key="index" class="mb-2">
                    <div class="d-flex align-items-center p-2 border rounded bg-white">
                      <div class="mr-2">
                        <img 
                          v-if="item.fileType === 'Image' || (item.url && /\.(jpg|jpeg|png|gif|webp|svg)(\?.*)?$/i.test(item.url))" 
                          :src="getFileUrl(item.url)" 
                          style="width: 45px; height: 45px; object-fit: cover;" 
                          class="rounded border" 
                        />
                        <i v-else-if="item.fileType === 'Video' || (item.url && /\.(mp4|mov|avi|webm)(\?.*)?$/i.test(item.url))" class="fa fa-file-video-o fa-2x text-primary px-2"></i>
                        <i v-else class="fa fa-file-text-o fa-2x text-secondary px-2"></i>
                      </div>
                      <div class="flex-grow-1 overflow-hidden">
                        <div class="text-truncate font-weight-bold small" :title="item.description || 'Attachment'">{{ item.description || 'Attachment' }}</div>
                        <a :href="getFileUrl(item.url)" target="_blank" class="text-info small text-decoration-none">
                          <i class="fa fa-external-link mr-1"></i>Open File
                        </a>
                      </div>
                    </div>
                  </b-col>
                </b-row>
              </div>
              <span v-else class="text-muted">No evidence links provided.</span>
            </b-col>
          </b-row>
        </div>

        <div v-if="isAdmin && crimeData.isAirSubmitted && !crimeData.isAirApproved" class="text-right">
          <b-button variant="success" @click="approveAIR" class="px-4">
            Approve AIR
          </b-button>
        </div>
      </div>

      <div v-else class="text-center py-5 text-muted">
        <i class="fa fa-info-circle fa-3x mb-3"></i>
        <p>No AIR has been submitted yet for this case.</p>
      </div>
    </va-card>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  props: ['crimeData', 'appUser'],
  data () {
    return {
      airData: {
        infoSummary: '',
        pointOfContact: '',
        location: {
          exactLocation: '',
        },
        supportingEvidence: [],
      },
      evidenceLinks: '',
      isUploading: false,
    }
  },
  computed: {
    isAdmin () {
      return this.appUser && this.appUser.usertype == 0
    },
  },
  methods: {
    getFileUrl (url) {
      if (!url) return ''
      if (url.startsWith('http://') || url.startsWith('https://')) {
        return url
      }
      const key = url.startsWith('/') ? url.substring(1) : url
      return (this.baseURL || 'http://localhost:4003/document/get/') + key
    },
    async handleFileUpload (event) {
      const files = event.target.files
      if (!files || files.length === 0) return

      this.isUploading = true
      try {
        for (let i = 0; i < files.length; i++) {
          const file = files[i]
          const formData = new FormData()
          formData.append('file', file)

          const response = await axios({
            method: 'POST',
            url: '/crimereport/media/upload',
            headers: {
              token: localStorage.getItem('token'),
              'Content-Type': 'multipart/form-data',
            },
            data: formData,
          })

          if (response.data.status) {
            let fileType = 'Document'
            if (file.type.startsWith('image/')) {
              fileType = 'Image'
            } else if (file.type.startsWith('video/')) {
              fileType = 'Video'
            }

            this.airData.supportingEvidence.push({
              fileType: fileType,
              url: response.data.key,
              description: response.data.originalname,
            })
          } else {
            this.$dlg.alert(response.data.msg || 'Upload failed', () => {}, { messageType: 'error' })
          }
        }
      } catch (err) {
        console.error(err)
        this.$dlg.alert('Error uploading file(s)', () => {}, { messageType: 'error' })
      } finally {
        this.isUploading = false
        // Reset file input value
        if (this.$refs.airFileInput) {
          this.$refs.airFileInput.value = ''
        }
      }
    },
    removeEvidence (index) {
      this.airData.supportingEvidence.splice(index, 1)
    },
    submitAIR () {
      if (confirm('Are you sure you want to submit this AIR for approval?')) {
        // Map evidence links to supportingEvidence array
        if (this.evidenceLinks) {
          const extraEvidence = this.evidenceLinks.split(',').map(link => ({
            fileType: 'Image',
            url: link.trim(),
            description: 'Manual Link',
          })).filter(item => item.url)
          this.airData.supportingEvidence = [...this.airData.supportingEvidence, ...extraEvidence]
        }

        axios({
          method: 'POST',
          url: '/crimereport/air/submit',
          headers: {
            token: localStorage.getItem('token'),
          },
          data: {
            id: this.crimeData._id,
            airData: this.airData,
          },
        })
          .then((response) => {
            if (response.data.status) {
              this.$dlg.alert(response.data.msg, () => {
                this.$emit('refresh')
              }, { messageType: 'success' })
            } else {
              this.$dlg.alert(response.data.msg, () => {}, { messageType: 'error' })
            }
          })
          .catch((err) => {
            console.error(err)
            this.$dlg.alert('Failed to submit AIR', () => {}, { messageType: 'error' })
          })
      }
    },
    approveAIR () {
      if (confirm('Are you sure you want to approve this AIR?')) {
        axios({
          method: 'POST',
          url: '/crimereport/air/approve',
          headers: {
            token: localStorage.getItem('token'),
          },
          data: {
            id: this.crimeData._id,
          },
        })
          .then((response) => {
            if (response.data.status) {
              this.$dlg.alert(response.data.msg, () => {
                this.$emit('refresh')
              }, { messageType: 'success' })
            } else {
              this.$dlg.alert(response.data.msg, () => {}, { messageType: 'error' })
            }
          })
          .catch((err) => {
            console.error(err)
            this.$dlg.alert('Failed to approve AIR', () => {}, { messageType: 'error' })
          })
      }
    },
    downloadPDF () {
      axios({
        method: 'POST',
        url: '/crimereport/air/export',
        headers: {
          token: localStorage.getItem('token'),
        },
        data: {
          id: this.crimeData._id,
        },
        responseType: 'blob',
      })
        .then((response) => {
          const fileURL = window.URL.createObjectURL(new Blob([response.data]))
          const link = document.createElement('a')
          link.href = fileURL
          link.download = `AIR_${this.crimeData.crimeReportID || 'Report'}.pdf`
          link.click()
        })
        .catch((err) => {
          console.error(err)
          this.$dlg.alert('Failed to download PDF', () => {}, { messageType: 'error' })
        })
    },
  },
}
</script>

<style scoped>
.font-weight-bold {
  font-weight: 600 !important;
}
</style>
